// explorerView — Explorer URL 쿼리스트링 → 화면 상태 / 목록 API 필터 변환 (단일 출처).
//
// 2026-10-03 — 이전엔 Explorer·FilterBar·각 섹션이 파라미터를 제각각 해석해서
//   스마트폴더가 깨져 있었다: 미분류(?group=null → NaN → 400), 휴지통(?trash=1 미전달),
//   즐겨찾기(?pinned=true 미인식), 최근 7일(?recent)·만료 임박(?expires) 무시.
//
// 지원 파라미터:
//   group=<id>|null   그룹 / 미분류        q, type, tag      검색·필터
//   pinned=true|1     즐겨찾기만            recent=7d         최근 N일 생성·수정
//   expires=<30d      만료 N일 이내         trash=1           휴지통
//   sort=<key>        정렬 (기본 name)      offset=<n>        페이지

import type { ItemSortKey, ItemType, ListItemsFilter } from '../api/items';

/** 기본 정렬 — 마스터 결정 2026-10-03: 이름순 (최근 사용은 별도 구역) */
export const DEFAULT_SORT: ItemSortKey = 'name';

// last_used_at은 목록 정렬에서 제외 (최근 사용 구역이 담당) — 예전 URL에 남아 있으면 기본값으로
const SORT_KEYS: ItemSortKey[] = ['name', 'created_at', 'expires_soon', 'use_count'];

export type SmartFolderKind = 'unfiled' | 'pinned' | 'recent' | 'expiring' | 'trash';

export interface ExplorerView {
  /** 숫자 그룹 (미분류·전체면 null) */
  groupId: number | null;
  /** 활성 스마트폴더 (없으면 null) */
  smart: SmartFolderKind | null;
  q?: string;
  type?: ItemType;
  tag?: string;
  /** 최근 N일 (smart=recent) */
  recentDays?: number;
  /** 만료 N일 이내 (smart=expiring) */
  expiringDays?: number;
  sort: ItemSortKey;
  offset: number;
  /** 검색·타입·태그 필터 활성 — 즐겨찾기·최근·그룹 구역을 숨기고 결과만 보여준다 */
  hasFilter: boolean;
}

/** '7d' → 7 (형식 불일치면 undefined) */
function parseDays(v: string | null, prefix = ''): number | undefined {
  if (!v) return undefined;
  const m = new RegExp(`^${prefix}(\\d{1,4})d$`).exec(v);
  return m ? Number(m[1]) : undefined;
}

/** URL → 화면 상태 */
export function parseExplorerView(sp: URLSearchParams): ExplorerView {
  const groupRaw = sp.get('group');
  const groupNum = groupRaw && /^\d+$/.test(groupRaw) ? Number(groupRaw) : null;

  const pinnedRaw = sp.get('pinned');
  const recentDays = parseDays(sp.get('recent'));
  const expiringDays = parseDays(sp.get('expires'), '<');

  let smart: SmartFolderKind | null = null;
  if (sp.get('trash') === '1') smart = 'trash';
  else if (groupRaw === 'null') smart = 'unfiled';
  else if (pinnedRaw === 'true' || pinnedRaw === '1') smart = 'pinned';
  else if (recentDays !== undefined) smart = 'recent';
  else if (expiringDays !== undefined) smart = 'expiring';

  const sortRaw = sp.get('sort') as ItemSortKey | null;
  const offsetRaw = sp.get('offset');

  const q = sp.get('q') || undefined;
  const type = (sp.get('type') as ItemType | null) || undefined;
  const tag = sp.get('tag') || undefined;

  return {
    groupId: groupNum,
    smart,
    q,
    type,
    tag,
    recentDays,
    expiringDays,
    sort: sortRaw && SORT_KEYS.includes(sortRaw) ? sortRaw : DEFAULT_SORT,
    offset: offsetRaw && /^\d+$/.test(offsetRaw) ? Number(offsetRaw) : 0,
    hasFilter: !!(q || type || tag),
  };
}

/** 화면 상태 → 메인 목록 API 필터 */
export function toListFilter(
  view: ExplorerView,
  opts: { limit: number; now?: number; groupScope?: 'descendants' },
): ListItemsFilter {
  const now = opts.now ?? Date.now();
  const qParts: string[] = [];
  if (view.q) qParts.push(view.q);
  // 만료 임박은 서버 검색 토큰 문법(expires:<Nd)으로 전달
  if (view.smart === 'expiring' && view.expiringDays !== undefined) qParts.push(`expires:<${view.expiringDays}d`);

  const filter: ListItemsFilter = {
    sort: view.sort,
    limit: opts.limit,
    offset: view.offset,
  };
  if (qParts.length > 0) filter.q = qParts.join(' ');
  if (view.type) filter.type = view.type;
  if (view.tag) filter.tag = view.tag;
  if (view.groupId !== null) {
    filter.group_id = view.groupId;
    if (opts.groupScope) filter.group_scope = opts.groupScope;
  }
  if (view.smart === 'unfiled') filter.unfiled = true;
  if (view.smart === 'pinned') filter.pinned = 1;
  if (view.smart === 'recent' && view.recentDays !== undefined) {
    filter.updated_since = now - view.recentDays * 86_400_000;
  }
  if (view.smart === 'trash') filter.trash = true;
  return filter;
}

/** 즐겨찾기·최근·그룹 구역을 보여줄지 (일반 탐색 화면일 때만) */
export function showsSections(view: ExplorerView): boolean {
  return !view.hasFilter && view.smart === null;
}

/** 메인 목록 구역 제목 */
export function listTitle(view: ExplorerView): string {
  if (view.hasFilter) return '검색·필터 결과';
  switch (view.smart) {
    case 'unfiled': return '미분류 항목';
    case 'pinned': return '즐겨찾기 항목';
    case 'recent': return `최근 ${view.recentDays}일 생성·수정`;
    case 'expiring': return `만료 ${view.expiringDays}일 이내`;
    case 'trash': return '휴지통';
    default: return view.groupId !== null ? '이 그룹의 항목' : '전체 항목';
  }
}
